import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
  Grid,
  Divider,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import { Close, Email, CheckCircle, Visibility, VisibilityOff } from '@mui/icons-material';
import { 
  createUserWithEmailAndPassword, 
  sendEmailVerification, 
  signInWithEmailAndPassword 
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';

const BuyerQuickRegistration = ({ open, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [isLoginMode, setIsLoginMode] = useState(true); // Commencer par le mode connexion
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEmailValidation, setShowEmailValidation] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      setError('Veuillez remplir tous les champs obligatoires');
      return false;
    }

    if (!isLoginMode) {
      // Validation supplémentaire pour l'inscription
      if (!formData.firstName || !formData.lastName || !formData.phone || !formData.city) {
        setError('Veuillez remplir tous les champs obligatoires');
        return false;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Les mots de passe ne correspondent pas');
        return false;
      }

      if (formData.password.length < 6) {
        setError('Le mot de passe doit contenir au moins 6 caractères');
        return false;
      }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Veuillez entrer une adresse email valide');
      return false;
    }

    return true;
  };

  // Fonction de connexion
  const handleLogin = async () => {
    try {
      setLoading(true);
      
      // Connexion avec Firebase Authentication
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      
      const user = userCredential.user;
      
      // Vérifier si l'utilisateur est un acheteur
      const userDoc = await getDoc(doc(db, "buyers", user.uid));
      
      if (!userDoc.exists()) {
        await auth.signOut();
        setError("Ce compte n'existe pas en tant qu'acheteur. Veuillez vous inscrire.");
        return;
      }
      
      const userData = userDoc.data();
      
      // Vérifier si l'email a été vérifié
      if (!user.emailVerified) {
        await auth.signOut();
        setError("Veuillez vérifier votre email avant de vous connecter");
        return;
      }
      
      // Mettre à jour le champ emailVerified dans Firestore si nécessaire
      if (!userData.emailVerified && user.emailVerified) {
        await setDoc(doc(db, "buyers", user.uid), {
          ...userData,
          emailVerified: true,
          updatedAt: new Date()
        }, { merge: true });
      }
      
      setSuccess('Connexion réussie !');
      
      // Fermer la modale et appeler onSuccess
      setTimeout(() => {
        handleClose();
        if (onSuccess) onSuccess();
      }, 1000);
      
    } catch (error) {
      console.error('Erreur lors de la connexion:', error);
      
      let errorMessage = 'Une erreur est survenue lors de la connexion';
      
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        errorMessage = 'Email ou mot de passe incorrect';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Trop de tentatives échouées. Veuillez réessayer plus tard';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fonction d'inscription
  const handleRegistration = async () => {
    try {
      setLoading(true);

      // Créer le compte utilisateur
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const user = userCredential.user;

      // Enregistrer les données de l'acheteur dans Firestore
      await setDoc(doc(db, 'buyers', user.uid), {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        company: '',
        createdAt: new Date(),
        emailVerified: false,
        status: 'pending' // En attente de vérification email
      });

      // Envoyer l'email de vérification
      await sendEmailVerification(user, {
        url: `${window.location.origin}/email-verification`,
      });

      setSuccess('Compte créé avec succès ! Un email de vérification a été envoyé.');
      
      // Afficher la modale de validation d'email après un délai
      setTimeout(() => {
        setShowEmailValidation(true);
      }, 1500);

    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('Cette adresse email est déjà utilisée');
          break;
        case 'auth/weak-password':
          setError('Le mot de passe est trop faible');
          break;
        case 'auth/invalid-email':
          setError('Adresse email invalide');
          break;
        default:
          setError('Une erreur est survenue lors de l\'inscription');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    if (isLoginMode) {
      await handleLogin();
    } else {
      await handleRegistration();
    }
  };

  const handleClose = () => {
    if (!loading) {
      setIsLoginMode(true); // Revenir au mode connexion
      setShowPassword(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        city: '',
        password: '',
        confirmPassword: ''
      });
      setError('');
      setSuccess('');
      setShowEmailValidation(false);
      onClose();
    }
  };

  const toggleMode = () => {
    setIsLoginMode(!isLoginMode);
    setError('');
    setSuccess('');
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      city: '',
      password: '',
      confirmPassword: ''
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleEmailValidationClose = () => {
    setShowEmailValidation(false);
    handleClose();
    if (onSuccess) onSuccess();
  };

  const handleGoToLogin = () => {
    setShowEmailValidation(false);
    handleClose();
    // Ici vous pouvez ajouter la logique pour ouvrir la modale de connexion
  };

  return (
    <>
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="div">
          {isLoginMode ? 'Connexion - Acheteur' : 'Inscription - Acheteur'}
        </Typography>
        <IconButton 
          onClick={handleClose}
          disabled={loading}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Créez rapidement votre compte pour passer commande
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {!isLoginMode && (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                   name="firstName"
                   label="Prénom *"
                   value={formData.firstName}
                   onChange={handleChange}
                   fullWidth
                   disabled={loading}
                   size="small"
                 />
                 <TextField
                   name="lastName"
                   label="Nom *"
                   value={formData.lastName}
                   onChange={handleChange}
                   fullWidth
                   disabled={loading}
                   size="small"
                 />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                   name="phone"
                   label="Numéro de téléphone *"
                   value={formData.phone}
                   onChange={handleChange}
                   fullWidth
                   disabled={loading}
                   size="small"
                   placeholder="Ex: +241 01 23 45 67"
                 />
                 <TextField
                   name="city"
                   label="Ville *"
                   value={formData.city}
                   onChange={handleChange}
                   fullWidth
                   disabled={loading}
                   size="small"
                   placeholder="Ex: Libreville"
                 />
              </Box>
            </>
          )}

          <TextField
             name="email"
             label="Email *"
             type="email"
             value={formData.email}
             onChange={handleChange}
             fullWidth
             disabled={loading}
             size="small"
             sx={{ mb: 2 }}
           />

           <TextField
             name="password"
             label="Mot de passe *"
             type={showPassword ? 'text' : 'password'}
             value={formData.password}
             onChange={handleChange}
             fullWidth
             disabled={loading}
             size="small"
             helperText={!isLoginMode ? "Minimum 6 caractères" : ""}
             sx={{ mb: 2 }}
             InputProps={{
               endAdornment: (
                 <InputAdornment position="end">
                   <IconButton
                     aria-label="toggle password visibility"
                     onClick={togglePasswordVisibility}
                     edge="end"
                     size="small"
                   >
                     {showPassword ? <VisibilityOff /> : <Visibility />}
                   </IconButton>
                 </InputAdornment>
               ),
             }}
           />

           {!isLoginMode && (
             <TextField
               name="confirmPassword"
               label="Confirmer le mot de passe *"
               type={showPassword ? 'text' : 'password'}
               value={formData.confirmPassword}
               onChange={handleChange}
               fullWidth
               disabled={loading}
               size="small"
               sx={{ mb: 2 }}
             />
           )}

          <Typography variant="caption" color="text.secondary">
            * Champs obligatoires
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <Button 
              onClick={handleClose}
              disabled={loading}
              color="inherit"
              sx={{ flex: 1 }}
            >
              Annuler
            </Button>
            <Button 
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
              sx={{ flex: 1 }}
            >
              {loading 
                ? (isLoginMode ? 'Connexion...' : 'Inscription...') 
                : (isLoginMode ? 'Se connecter' : 'S\'inscrire')
              }
            </Button>
          </Box>
          
          <Divider sx={{ width: '100%', my: 1 }} />
          
          <Box sx={{ textAlign: 'center', width: '100%' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {isLoginMode ? 'Pas encore de compte ?' : 'Vous avez déjà un compte ?'}
            </Typography>
            <Button 
              variant="text"
              color="primary"
              onClick={toggleMode}
              disabled={loading}
              sx={{ 
                fontWeight: 'bold'
              }}
            >
              {isLoginMode ? 'S\'inscrire' : 'Se connecter'}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>

    {/* Modale de validation d'email */}
    <Dialog
      open={showEmailValidation}
      onClose={handleEmailValidationClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          textAlign: 'center'
        }
      }}
    >
      <DialogContent sx={{ py: 4 }}>
        <CheckCircle 
          sx={{ 
            fontSize: 80, 
            color: 'success.main', 
            mb: 2 
          }} 
        />
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Inscription réussie !
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Un email de vérification a été envoyé à :
        </Typography>
        <Typography variant="h6" sx={{ color: 'primary.main', mb: 3 }}>
          {formData.email}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Veuillez vérifier votre boîte de réception et cliquer sur le lien de vérification pour activer votre compte.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Vous pourrez ensuite vous connecter et passer vos commandes.
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 2 }}>
        <Button 
          variant="outlined"
          onClick={handleEmailValidationClose}
          sx={{ minWidth: 120 }}
        >
          Fermer
        </Button>
        <Button 
          variant="contained"
          onClick={handleGoToLogin}
          sx={{ minWidth: 120 }}
        >
          Se connecter
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default BuyerQuickRegistration;